import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  columnProperties,
  getHeadingMatchedPropertyKey,
  importDataContainsHeader,
  mapMatchedColumnsToRowData,
} from './helpers';
import Select from '../../../../../../../../components/global/Select';
import Button from '../../../../../../../../components/global/Button';
import ColumnPreview from './ColumnPreview';
import CheckboxIcon from '../../../../../../../../components/icons/CheckboxIcon';
import useVirtualPageView from '../../../../../../services/analytics/useVirtualPageView';
import CustomControl from '../../../../../../../../components/global/CustomControl';
import { ALBUM_SUBTYPE } from '../../../../../../../../constants/albums';
import UniformConfigurationSelector from './components/UniformConfigurationSelector';
import ContactLinkSelector from './components/ContactLinkSelector';
import ContextualString from '../../../../../../../../components/contextual/ContextualString';
import {
  isPhotoFinderCreditsDeliveryEnabled,
  isParentPayDeliveryEnabled,
  isPhotoFinderCreditsPPGDeliveryEnabled,
} from '../../../../../../../../util/album-folder';

const propTypes = {
  data: PropTypes.arrayOf(PropTypes.array),
  loading: PropTypes.bool,
  onContinue: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  contactLabel: PropTypes.string,
  forHierarchyItems: PropTypes.bool,
  albumSubType: PropTypes.oneOf(Object.values(ALBUM_SUBTYPE)),
  albumFolder: PropTypes.object,
  supportedColumnProperties: PropTypes.arrayOf(PropTypes.string),
  isYearbookEvent: PropTypes.bool,
  ownerPurchasedJoinsEnabled: PropTypes.bool,
};

const ColumnMatcher = ({
  onContinue,
  onCancel,
  data,
  loading,
  contactLabel = 'contact',
  forHierarchyItems = false,
  isPhotoFinder = false,
  isPro = false,
  albumSubType,
  albumFolder,
  supportedColumnProperties = columnProperties,
  isYearbookEvent = false,
  ownerPurchasedJoinsEnabled = false,
}) => {
  useVirtualPageView({
    title: 'Import Contacts - Map Columns',
    path: '/contacts-import/map',
  });

  const isClaimOnlyPro = isPro && !isPhotoFinder;

  const isPFCredits = albumFolder
    ? isPhotoFinderCreditsDeliveryEnabled(albumFolder)
    : false;
  const isPPAOnly = albumFolder
    ? isParentPayDeliveryEnabled(albumFolder)
    : false;
  const ISPPG = albumFolder
    ? isPhotoFinderCreditsPPGDeliveryEnabled(albumFolder)
    : false;

  const shouldForceUnclaimed = isPPAOnly || ISPPG;

  const claimedDefault = (() => {
    if (shouldForceUnclaimed) return false;
    if (isPFCredits) return true;
    return null;
  })();

  const [claimed, setClaimed] = useState(claimedDefault);

  const purchaseDefault = (() => {
    if (shouldForceUnclaimed) return false;
    if (isPFCredits) return true;
    return null;
  })();

  const shouldShowClaimedToggle =
    isPFCredits || (claimedDefault === null && purchaseDefault === null);

  const [grantPurchase, setGrantPurchase] = useState(purchaseDefault);
  const [uniformConfiguration, setUniformConfiguration] = useState(undefined);
  const [contactLinkConfiguration, setContactLinkConfiguration] = useState({
    albumFolderId: null,
    albums: [],
  });

  const hasHeader = importDataContainsHeader(supportedColumnProperties, data);
  const [header, ...rows] = data;
  const [columns, setColumns] = useState(
    hasHeader
      ? header.map((heading, index) => {
          return {
            label: heading,
            rowDataIndex: index,
            matchedPropertyKey: getHeadingMatchedPropertyKey(
              supportedColumnProperties,
              heading,
            ),
          };
        })
      : // If there's no header simply start with no selections and let the user manually map the columns.
        header.map((_, index) => ({
          label: 'N/A (no header detected)',
          rowDataIndex: index,
          matchedPropertyKey: '',
        })),
  );
  const [error, setError] = useState('');

  const allColumnsMapped = columns.every(column => !!column.matchedPropertyKey);

  const onPropertyChange = (selectedColumn, selectedPropertyKey) => {
    // Do nothing, the user selected the same property.
    if (selectedColumn.matchedPropertyKey === selectedPropertyKey) {
      return;
    }

    setColumns(prevColumns =>
      prevColumns.map(column => {
        if (column.rowDataIndex === selectedColumn.rowDataIndex) {
          return {
            ...column,
            matchedPropertyKey: selectedPropertyKey,
          };
        }

        // Reset the property that had occupied before
        if (
          column.matchedPropertyKey === selectedPropertyKey &&
          selectedPropertyKey !== 'skip'
        ) {
          return {
            ...column,
            matchedPropertyKey: '',
          };
        }

        return column;
      }),
    );
  };

  const onSubmit = () => {
    if (!allColumnsMapped) {
      setError('Please select a property or "don’t import" for every column.');
      return;
    }

    if (
      isYearbookEvent &&
      (!columns.find(col => col.matchedPropertyKey === 'firstName') ||
        !columns.find(col => col.matchedPropertyKey === 'lastName') ||
        !columns.find(col => col.matchedPropertyKey === 'grade') ||
        (!columns.find(col => col.matchedPropertyKey === 'organizationRefId') &&
          !columns.find(col => col.matchedPropertyKey === 'selfieFilename') &&
          !columns.find(col => col.matchedPropertyKey === 'selfiePhotoUrl')))
    ) {
      setError(
        'Full name, grade, and photo filename are required for Yearbook Imports',
      );
      return;
    }

    onContinue(
      mapMatchedColumnsToRowData(columns, hasHeader ? rows : data),
      claimed,
      grantPurchase,
      uniformConfiguration,
      contactLinkConfiguration,
    );
  };

  return (
    <div className="column-matcher-wrap">
      <header className="column-matcher-header">
        <h4 className="mb-1">
          Map columns in your file to {contactLabel} properties
        </h4>
        <p>
          Each column header below should be mapped to a {contactLabel}{' '}
          property. Some of these have already been mapped based on their names.
          Anything that hasn&rsquo;t been mapped yet can be manually mapped to a{' '}
          {contactLabel} property with the dropdown menu. You can always choose
          &ldquo;Don&rsquo;t import column&rdquo; for unnecessary columns.
        </p>

        {forHierarchyItems && (
          <p className="text-sm my-1">
            <b>Reminder</b>: If you have reference images, please upload them
            via the <b>Manager Members</b> drop down by clicking the{' '}
            <b>Manage reference images</b> link. The filenames of your images
            should match the filename column in your CSV upload in order to be
            properly mapped.
          </p>
        )}

        <UniformConfigurationSelector
          contactLabel={contactLabel}
          uniformConfiguration={uniformConfiguration}
          setUniformConfiguration={setUniformConfiguration}
        />
        <ContactLinkSelector
          contactLabel={contactLabel}
          contactLinkConfiguration={contactLinkConfiguration}
          setContactLinkConfiguration={setContactLinkConfiguration}
        />
      </header>

      <table
        className={classNames({
          'table table-striped table-lg table-column-matcher': true,
          'is-loading': loading,
        })}
      >
        <thead>
          <tr>
            <th>Matched</th>
            <th className="text-left">Column Name</th>
            <th className="text-left">Preview</th>
            <th className="text-left text-capitalize">
              {contactLabel} Property
            </th>
          </tr>
        </thead>

        <tbody>
          {columns.map((column, columnIndex) => {
            return (
              <tr key={columnIndex}>
                <td>
                  {column.matchedPropertyKey ? <CheckboxIcon size={16} /> : '-'}
                </td>
                <td className="text-left">{column.label}</td>
                <td className="text-left column-matcher-preview">
                  <ColumnPreview
                    index={columnIndex}
                    rows={hasHeader ? rows : data}
                  />
                </td>
                <td>
                  <Select
                    className="m-0"
                    label="Select a property"
                    labelSrOnly
                    id={column.label}
                    value={column.matchedPropertyKey}
                    onChange={event => {
                      onPropertyChange(column, event.target.value);
                    }}
                  >
                    <option value="">Select a property</option>
                    <option value="skip">Don&rsquo;t import</option>
                    {supportedColumnProperties.map(property => {
                      const isSelectedElsewhere =
                        columns.some(
                          x => x.matchedPropertyKey === property.key,
                          // Nit: Only show the selected checkbox if it's
                          // not the currently selected option.
                        ) && property.key !== column.matchedPropertyKey;

                      return (
                        <option key={property.key} value={property.key}>
                          {property.label} {isSelectedElsewhere && '✓'}
                        </option>
                      );
                    })}
                  </Select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {forHierarchyItems && shouldShowClaimedToggle && (
        <div className="w-100 d-flex flex-column justify-content-end my-2 pr-2">
          <h4 className="my-1 ml-1 text-bolder">
            <ContextualString
              subtype={albumSubType}
              id="columnMatcherHeader"
              defaultMessage="Do you want to automatically send proofs to the members you upload?"
            />
          </h4>
          <div className="d-flex align-items-center mb-2 ml-2">
            <CustomControl
              id="claimed-subbed"
              type="radio"
              name="claimed-subbed"
              checked={
                claimed === true && (grantPurchase === true || isClaimOnlyPro)
              }
              onChange={() => {
                setClaimed(true);
                if (!isClaimOnlyPro) {
                  setGrantPurchase(true);
                }
              }}
              label={
                <ContextualString
                  subtype={albumSubType}
                  id="columnMatcherClaimedLabel"
                  defaultMessage="Yes, send matching photos to members automatically"
                />
              }
              size="md"
              className="d-inline"
            />
          </div>
          <div className="d-flex align-items-center ml-2">
            <CustomControl
              id="unclaimed-unsubbed"
              type="radio"
              name="unclaimed-unsubbed"
              checked={
                claimed === false && (grantPurchase === false || isClaimOnlyPro)
              }
              onChange={() => {
                setClaimed(false);
                if (!isClaimOnlyPro) {
                  setGrantPurchase(false);
                }
              }}
              label={
                <ContextualString
                  subtype={albumSubType}
                  id="columnMatcherUnclaimedLabel"
                  defaultMessage="No, wait for members to onboard prior to sending matching photos"
                />
              }
              size="md"
              className="d-inline"
            />
          </div>
          {ownerPurchasedJoinsEnabled && isClaimOnlyPro && (
            <Fragment>
              <hr className="w-100" />
              <div className="d-flex align-items-center mb-2 ml-2">
                <CustomControl
                  id="grant-purchase-indv"
                  type="checkbox"
                  name="grant-purchase-indv"
                  checked={grantPurchase}
                  onClick={() => {
                    setGrantPurchase(prev => !prev);
                  }}
                  label="Grant purchase to all imported members"
                  size="md"
                  className="d-inline"
                />
              </div>
            </Fragment>
          )}
        </div>
      )}

      <footer className="column-matcher-footer">
        {error && <p className="text-danger text-sm mb-0">{error}</p>}

        {onCancel && (
          <Button
            onClick={onCancel}
            btnStyle="link"
            className="ml-2"
            disabled={loading}
            btnSize="sm"
          >
            Cancel Import
          </Button>
        )}
        <Button
          onClick={onSubmit}
          className="ml-2"
          disabled={
            loading ||
            (forHierarchyItems && grantPurchase === null && claimed === null)
          }
          btnSize="sm"
        >
          {loading ? 'Processing...' : 'Continue'}
        </Button>
      </footer>
    </div>
  );
};

ColumnMatcher.propTypes = propTypes;

export default ColumnMatcher;
